import flatten from '@opten/array/flatten';
import { Component } from '@opten/compiler/metadata/component';
import getParentWithClass from '@opten/dom/get-parent-with-class';
import toggleClass from '@opten/dom/toggle-class';
import removeProperty from '@opten/object/remove-property';
import { Renderer } from '@opten/renderer';
import { AttributeConverter } from '@opten/renderer/converters/attribute.converter';
import { DefaultConverter } from '@opten/renderer/converters/default.converter';
import { IfConverter } from '@opten/renderer/converters/if.converter';
import { InputConverter } from '@opten/renderer/converters/input.converter';
import { RepeatInnerConverter } from '@opten/renderer/converters/repeat.inner.converter';
import { combineLatest, from, Observable, of as observableOf, of } from 'rxjs';
import { concatMap, filter, first, map, mergeMap, switchMap, take, takeLast } from 'rxjs/operators';
import { distinctUntilUserChanged } from '../../utilities/observable.helper';
import { BaseBasket, BaseBasketOptions } from '../basket/basket-base';
import { Basket, BasketProductItem } from '../models/basket';
import { Location, User } from '../models/customer';
import { isPartner } from '../models/roles';
import { basketStorage, memberStorage } from './shop-context';

export interface ShopCategoryComponentOptions extends BaseBasketOptions {
	shop?: {
		signInTeaser: string;
		addBulk: string;
		locationSelect: string;
	};
}

@Component({ className: 'js-basket-shop-category', observe: true })
export class ShopCategoryComponent extends BaseBasket {
	private _renderer: Renderer;
	private _locationElement: HTMLElement;
	private _locationSelectElement: HTMLSelectElement;
	private _signInTeaserElement: HTMLElement;
	private _userLocation: {
		currentLocation?: number;
		shippingLocations?: Location[];
	};

	options: ShopCategoryComponentOptions = {
		selectors: {
			location: 'js-location',
			product: 'js-product',
			productImage: 'js-product-image',
			productVariant: 'js-product-variant',
			productVariantQuantity: 'js-product__quantity',
			productVariantPrice: 'js-product__price'
		},
		shop: {
			signInTeaser: 'js-sign-in-teaser',
			addBulk: 'js-basket-add-bulk',
			locationSelect: 'js-location__select'
		},
		target: 'data-target',
		id: 'data-id',
		name: 'data-name',
		disabled: '--disabled'
	};

	constructor(element: HTMLElement) {
		super(element);

		this._locationElement = element.getElementsByClassName(this.options.selectors.location)[0] as HTMLElement;
		this._locationSelectElement = this._locationElement
			? (this._locationElement.getElementsByClassName(this.options.shop.locationSelect)[0] as HTMLSelectElement)
			: null;
		this._signInTeaserElement = document.getElementsByClassName(this.options.shop.signInTeaser)[0] as HTMLElement;
		this._renderer = new Renderer([DefaultConverter, IfConverter, InputConverter, RepeatInnerConverter, AttributeConverter]);
	}

	onInit(): void {
		this.shopContext.onAuthenticationChanged.pipe(distinctUntilUserChanged()).subscribe(user => this.initLocation(user));

		this.shopContext.completeContext().pipe(first()).subscribe();

		this.initAddBulks();
	}

	initAddBulks(checkForProducts = false): void {
		const addBulks = Array.from(this.basketElement.getElementsByClassName(this.options.shop.addBulk));
		if (addBulks && addBulks.length > 0) {
			addBulks.forEach(addBulk => {
				const isPromotionPackage = addBulk.hasAttribute('data-is-promotion-package');
				if (checkForProducts) {
					const target = addBulk.getAttribute(this.options.target);
					const wrapperElement = getParentWithClass(addBulk as HTMLElement, target);
					this.noProductDisableBasket(wrapperElement);
				}

				addBulk.addEventListener('click', e => {
					const target = addBulk.getAttribute(this.options.target);
					const cleanBulkAfterAdding = addBulk.hasAttribute('data-add-bulk-clean')
						? addBulk.getAttribute('data-add-bulk-clean') === 'true'
						: true;
					const wrapperElement = getParentWithClass(addBulk as HTMLElement, target);

					this.addBulk(wrapperElement, isPromotionPackage)
						.pipe(
							filter(b => !!b),
							takeLast(1)
						)
						.subscribe(basket => {
							this.shopContext.basketUpdated(basket, true);
							if (cleanBulkAfterAdding) {
								this.resetAllProductVariantQuantities(wrapperElement);
								this.noProductDisableBasket(wrapperElement);
							}
						});
				});
			});
		}
	}

	private initLocation(user: User): void {
		this._userLocation = {};
		combineLatest(user ? this.shopContext.onIsGuest : observableOf(true), observableOf(user))
			.pipe(
				filter(([isGuest, user]) => user && isGuest !== null),
				switchMap(([isGuest, user]) => {
					if (user) {
						//only update location if not guest else set location to -1!!!
						if (isGuest === false && isPartner(user) === false) {
							if (user.contractualPartner && user.contractualPartner.multiLocations) {
								if (!user.shippingLocations || user.shippingLocations.length < 0) {
									const shippingLocations = flatten(
										user.contractualPartner.multiLocations.map(ml => ml.locations)
									) as Location[];

									for (let i = 0; i < shippingLocations.length; i++) {
										shippingLocations[i] = removeProperty(shippingLocations[i], 'devices') as Location;
										shippingLocations[
											i
										].addressPreview = `${shippingLocations[i].name} - ${shippingLocations[i].addresses[0].zip} ${shippingLocations[i].addresses[0].location}`;
									}

									this._userLocation.shippingLocations = shippingLocations;
									this._userLocation.currentLocation = shippingLocations[0].id;
									return combineLatest(
										memberStorage.updateShippingLocations(shippingLocations),
										memberStorage.updateCurrentLocation(shippingLocations[0])
									).pipe(map((user1, user2) => user));
								} else {
									this._userLocation.shippingLocations = user.shippingLocations;
									this._userLocation.currentLocation = user.currentLocation || user.shippingLocations[0].id;
									return observableOf(user);
								}
							}
						} else {
							const guestLocation: Location = {
								id: -1
							};
							this._userLocation.currentLocation = guestLocation.id;
							return memberStorage.updateCurrentLocation(guestLocation);
						}
					} else {
						return observableOf(user);
					}
				}),
				take(1)
			)
			.subscribe(() => this.loadUserLocation());
	}

	private loadUserLocation() {
		if (this._locationElement) {
			if (this._locationSelectElement) {
				this._locationElement.addEventListener('finished.opten.renderer', () => {
					this._locationSelectElement = this.reinitLocationSelect(this._locationElement, this._locationSelectElement);
				});
			}
			this._renderer.render(this._locationElement, this._userLocation, this);
		}
		if (this._signInTeaserElement) {
			this._renderer.render(this._signInTeaserElement, this._userLocation, this);
		}
	}

	updateBasketLocation(event: Event) {
		const element = (event.currentTarget || event.target) as HTMLInputElement;
		if (element && element.value) {
			const selectedLocation = { id: +element.value };
			if (this._userLocation.shippingLocations && this._userLocation.shippingLocations.length > 1) {
				for (let i = 0; i < this._userLocation.shippingLocations.length; i++) {
					this._userLocation.shippingLocations[i].selected =
						this._userLocation.shippingLocations[i].id.toString() === element.value;
				}
				memberStorage
					.updateShippingLocations(this._userLocation.shippingLocations)
					.pipe(
						mergeMap(userLocations => {
							return memberStorage.updateCurrentLocation(selectedLocation);
						})
					)
					.subscribe(user => {
						this._userLocation.currentLocation = user.currentLocation;
						this.loadUserLocation();
					});
			} else {
				memberStorage.updateCurrentLocation(selectedLocation).subscribe(user => {
					this._userLocation.currentLocation = user.currentLocation;
					this.loadUserLocation();
				});
			}
		}
	}

	reinitProductVariantQuantities(elements: HTMLElement[]) {
		if (elements && elements.length > 0) {
			elements.forEach(productVariantQuantity =>
				productVariantQuantity.addEventListener('change', e => {
					const target = productVariantQuantity.getAttribute(this.options.target);
					const wrapperElement = getParentWithClass(productVariantQuantity as HTMLElement, target);
					this.noProductDisableBasket(wrapperElement);
				})
			);
		}
	}

	private resetAllProductVariantQuantities(wrapperElement?: HTMLElement) {
		if (wrapperElement === null || wrapperElement === undefined) {
			wrapperElement = this.basketElement;
		}
		const productVariantQuantities = Array.from(wrapperElement.getElementsByClassName(this.options.selectors.productVariantQuantity));
		if (productVariantQuantities && productVariantQuantities.length > 0) {
			productVariantQuantities.forEach(productVariantQuantity => {
				(productVariantQuantity as HTMLInputElement).value = '0';
			});
		}
	}

	private noProductDisableBasket(wrapperElement?: HTMLElement) {
		if (wrapperElement === null || wrapperElement === undefined) {
			wrapperElement = this.basketElement;
		}
		let hasValue = false;
		const productVariantQuantities = Array.from(wrapperElement.getElementsByClassName(this.options.selectors.productVariantQuantity));
		if (productVariantQuantities && productVariantQuantities.length > 0) {
			productVariantQuantities.forEach(productVariantQuantity => {
				const value = (productVariantQuantity as HTMLInputElement).value;
				if (value !== '' && value !== '0') {
					hasValue = true;
					return false;
				}
			});
		}
		const closestAddBulk = wrapperElement.getElementsByClassName(this.options.shop.addBulk)[0];
		if (closestAddBulk) {
			toggleClass(closestAddBulk, this.options.disabled, !hasValue);
		}
	}

	private addBulk(wrapperElement?: HTMLElement, isPromotionPackage = false): Observable<Basket> {
		if (wrapperElement === null || wrapperElement === undefined) {
			wrapperElement = this.basketElement;
		}
		const addMultipleLocations = wrapperElement.getAttribute('data-add-bulk-multiple-locations') === 'true';
		if (addMultipleLocations) {
			const locations = Array.from(wrapperElement.querySelectorAll('.js-location')) as HTMLElement[];
			basketStorage.clear();
			return from(locations).pipe(concatMap(l => this.addBulkSingle(l, true, isPromotionPackage)));
		} else {
			return this.addBulkSingle(wrapperElement, false, isPromotionPackage);
		}
	}

	private addBulkSingle(wrapperElement?: HTMLElement, addMultipleLocations?: boolean, isPromotionPackage = false): Observable<Basket> {
		const selectedProducts: BasketProductItem[] = [];
		const products = Array.from(wrapperElement.getElementsByClassName(this.options.selectors.product));
		if (products && products.length > 0) {
			products.forEach((product: HTMLElement) => {
				if (product.offsetParent || addMultipleLocations) {
					if (isPromotionPackage) {
						const converted: BasketProductItem = this.convertPromotionPackage(product as HTMLElement);
						if (converted && converted.id && !selectedProducts.some(p => p.id === converted.id)) {
							selectedProducts.push(converted);
						}
					} else {
						const converted: BasketProductItem = this.convertProduct(product as HTMLElement, true);
						if (converted && converted.id && !selectedProducts.some(p => p.id === converted.id)) {
							selectedProducts.push(converted);
						}
					}
				}
			});
		}

		if (selectedProducts && selectedProducts.length) {
			return basketStorage.addBulk(selectedProducts, this.getLocation(wrapperElement));
		}

		return of(null);
	}
}
